import { useState, useEffect } from 'react';
import ShareJourneyCard from '../ShareJourneyCard';
import { API_URL } from './assets';

export function PuzzleMasteryDrawer({ pieces, studiedPieces, onClose, onSelectPiece, discount }) {
  const [rewards, setRewards] = useState(null);
  const [expandedReward, setExpandedReward] = useState(null);
  const [showUnlockCelebration, setShowUnlockCelebration] = useState(false);
  const [showShareCard, setShowShareCard] = useState(false);

  const corners = pieces.filter(p => p.piece_type === 'corner');
  const borders = pieces.filter(p => p.piece_type === 'border');
  const framePieces = [...corners, ...borders];
  // Only count studied pieces that exist in the current pieces array
  const pieceIds = new Set(pieces.map(p => p.piece_id));
  const relevantStudied = studiedPieces.filter(id => pieceIds.has(id));
  const studiedCount = relevantStudied.length;
  const totalCount = pieces.length;
  const frameStudiedCount = framePieces.filter(p => studiedPieces.includes(p.piece_id)).length;
  const frameTotalCount = framePieces.length;
  const pct = totalCount > 0 ? Math.min(Math.round((studiedCount / totalCount) * 100), 100) : 0;
  const isComplete = studiedCount >= totalCount && totalCount > 0;
  const isFrameComplete = frameStudiedCount >= frameTotalCount && frameTotalCount > 0;

  // Fetch rewards
  useEffect(() => {
    const fetchRewards = async () => {
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('access_token');
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(`${API_URL}/api/rewards/completion`, {
          headers, credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setRewards(data);
          if (data.first_unlock) {
            setShowUnlockCelebration(true);
            setTimeout(() => setShowUnlockCelebration(false), 5000);
          }
        }
      } catch { /* silent */ }
    };
    fetchRewards();
  }, []);

  const ICON_MAP = {
    scroll: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    ),
    flame: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
      </svg>
    ),
    key: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
      </svg>
    ),
  };

  // Map pieces to frame positions
  const framePositions = [
    { id: corners[0]?.piece_id, label: 'TL', pos: 'col-start-1 row-start-1', piece: corners[0] },
    { id: borders[0]?.piece_id, label: 'T', pos: 'col-start-2 row-start-1', piece: borders[0] },
    { id: corners[1]?.piece_id, label: 'TR', pos: 'col-start-3 row-start-1', piece: corners[1] },
    { id: borders[5]?.piece_id, label: 'L-T', pos: 'col-start-1 row-start-2', piece: borders[5] },
    { id: borders[1]?.piece_id, label: 'R-T', pos: 'col-start-3 row-start-2', piece: borders[1] },
    { id: borders[4]?.piece_id, label: 'L-B', pos: 'col-start-1 row-start-3', piece: borders[4] },
    { id: borders[2]?.piece_id, label: 'R-B', pos: 'col-start-3 row-start-3', piece: borders[2] },
    { id: corners[2]?.piece_id, label: 'BL', pos: 'col-start-1 row-start-4', piece: corners[2] },
    { id: borders[3]?.piece_id, label: 'B', pos: 'col-start-2 row-start-4', piece: borders[3] },
    { id: corners[3]?.piece_id, label: 'BR', pos: 'col-start-3 row-start-4', piece: corners[3] },
  ].filter(fp => fp.piece);

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="mastery-backdrop" />

      {/* Drawer */}
      <div className="fixed right-0 top-0 bottom-0 z-50 w-full sm:w-[400px] mastery-slide-in"
        data-testid="mastery-drawer"
        style={{ background: 'linear-gradient(180deg, #0c0a06 0%, #080704 50%, #060503 100%)' }}>

        {/* Header */}
        <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: 'rgba(120,80,20,0.15)' }}>
          <div>
            <p className="text-[9px] tracking-[0.3em] uppercase font-mono text-amber-700/50 mb-1">Puzzle Board</p>
            <h3 className="text-lg font-serif font-bold text-amber-200">Your Mastery</h3>
          </div>
          <button onClick={onClose} data-testid="btn-close-mastery"
            className="w-8 h-8 rounded-full flex items-center justify-center text-amber-700 hover:text-amber-300 transition-colors"
            style={{ background: 'rgba(30,20,10,0.5)' }}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto" style={{ height: 'calc(100% - 72px)' }}>
          {/* Progress Ring */}
          <div className="px-5 pt-6 pb-4 flex items-center gap-5">
            <div className="relative w-20 h-20 flex-shrink-0">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(120,80,20,0.15)" strokeWidth="6" />
                <circle cx="50" cy="50" r="42" fill="none"
                  stroke={isComplete ? '#10b981' : '#d97706'}
                  strokeWidth="6" strokeLinecap="round"
                  strokeDasharray={`${pct * 2.64} ${264 - pct * 2.64}`}
                  style={{ transition: 'stroke-dasharray 1s ease' }} />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-lg font-bold font-mono" style={{ color: isComplete ? '#34d399' : '#fbbf24' }}>
                  {pct}%
                </span>
              </div>
            </div>
            <div>
              <p className="text-sm font-serif text-amber-200">
                <strong className="text-amber-100">{studiedCount}</strong> of <strong className="text-amber-100">{totalCount}</strong> pieces placed
              </p>
              {frameTotalCount < totalCount && (
                <p className="text-[10px] text-amber-600/40 font-serif mt-0.5">
                  Frame: {frameStudiedCount}/{frameTotalCount} {isFrameComplete ? '(Complete!)' : ''}
                </p>
              )}
              <p className="text-[11px] text-amber-700/50 font-serif mt-1">
                {isComplete
                  ? 'The frame is complete. The picture is revealed.'
                  : isFrameComplete
                  ? `Frame complete! ${totalCount - studiedCount} inner piece${totalCount - studiedCount > 1 ? 's' : ''} remaining`
                  : `${frameTotalCount - frameStudiedCount} frame piece${frameTotalCount - frameStudiedCount > 1 ? 's' : ''} remaining`}
              </p>
              {discount && discount.discount_pct > 0 && (
                <p className="text-[11px] text-emerald-400/70 font-mono mt-1">
                  {discount.discount_pct}% discount earned
                </p>
              )}
              {isFrameComplete && (
                <button onClick={() => setShowShareCard(true)} data-testid="btn-share-journey"
                  className="mt-2 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-serif font-semibold transition-all hover:scale-105"
                  style={{ background: 'linear-gradient(135deg, rgba(217,119,6,0.15), rgba(16,185,129,0.1))', border: '1px solid rgba(217,119,6,0.25)', color: '#fbbf24' }}>
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  Share Your Journey
                </button>
              )}
            </div>
          </div>

          {/* Visual Puzzle Frame */}
          <div className="px-5 pb-5">
            <p className="text-[9px] tracking-[0.2em] uppercase font-mono text-amber-500/70 mb-3">The Frame</p>
            <div className="rounded-xl p-4 border" style={{ background: 'rgba(15,10,5,0.6)', borderColor: 'rgba(120,80,20,0.12)' }}>
              <div className="grid grid-cols-3 grid-rows-4 gap-2.5" data-testid="mastery-frame">
                {framePositions.map((fp, idx) => {
                  const isStudied = studiedPieces.includes(fp.id);
                  const isCorner = fp.piece?.piece_type === 'corner';
                  const pieceIndex = pieces.findIndex(p => p.piece_id === fp.id);
                  const color = fp.piece?.color || '#d97706';

                  return (
                    <button
                      key={fp.id}
                      data-testid={`mastery-piece-${fp.id}`}
                      onClick={() => isStudied && pieceIndex >= 0 && onSelectPiece(pieceIndex)}
                      disabled={!isStudied}
                      className={`relative rounded-lg flex flex-col items-center justify-center transition-all duration-300 ${
                        isStudied ? 'piece-snap-in cursor-pointer hover:scale-105' : 'cursor-default'
                      } ${isCorner ? 'h-20' : 'h-16'}`}
                      style={{
                        '--piece-color': `${color}40`,
                        background: isStudied
                          ? `radial-gradient(ellipse at center, ${color}18, ${color}08)`
                          : 'rgba(20,15,8,0.4)',
                        border: `1.5px ${isStudied ? 'solid' : 'dashed'} ${isStudied ? color + '50' : 'rgba(80,60,20,0.15)'}`,
                        animationDelay: `${idx * 0.08}s`,
                      }}
                    >
                      {isStudied ? (
                        <>
                          <img src="/scroll.jpeg" alt="" className="w-7 h-7 rounded object-cover mb-1 opacity-80" />
                          <span className="text-[8px] font-serif text-center leading-tight px-1" style={{ color: color + 'cc' }}>
                            {fp.piece?.reference?.split(' ').slice(0, 2).join(' ')}
                          </span>
                          <div className="absolute top-1 right-1">
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill={color} stroke="none">
                              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="w-7 h-7 rounded border border-dashed flex items-center justify-center mb-1"
                            style={{ borderColor: 'rgba(80,60,20,0.2)' }}>
                            <span className="text-[10px] text-amber-900/30">?</span>
                          </div>
                          <span className="text-[8px] font-serif text-amber-600/50 text-center leading-tight px-1">
                            {isCorner ? 'Corner' : 'Border'}
                          </span>
                        </>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Center label */}
              <div className="flex items-center justify-center mt-3">
                <div className="px-4 py-2 rounded-lg border text-center"
                  style={{ background: 'rgba(10,8,4,0.5)', borderColor: 'rgba(80,60,20,0.1)' }}>
                  <p className="text-[9px] font-serif italic" style={{ color: isComplete ? '#34d399' : '#a08040' }}>
                    {isComplete ? '"The picture is now complete."' : '"Build the frame first, then fill in the picture."'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Piece List */}
          <div className="px-5 pb-8">
            <p className="text-[9px] tracking-[0.2em] uppercase font-mono text-amber-500/70 mb-3">All Pieces</p>
            <div className="space-y-1.5">
              {pieces.map((piece, idx) => {
                const isStudied = studiedPieces.includes(piece.piece_id);
                return (
                  <button
                    key={piece.piece_id}
                    data-testid={`mastery-list-${piece.piece_id}`}
                    onClick={() => { onSelectPiece(idx); }}
                    className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 hover:bg-amber-900/10 group"
                    style={{ borderLeft: `2px solid ${isStudied ? piece.color : 'transparent'}` }}
                  >
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all"
                      style={{
                        background: isStudied ? `${piece.color}20` : 'rgba(30,20,10,0.3)',
                        border: `1.5px solid ${isStudied ? piece.color + '60' : 'rgba(80,60,20,0.15)'}`,
                      }}>
                      {isStudied ? (
                        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke={piece.color} strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <div className="w-1.5 h-1.5 rounded-full" style={{ background: 'rgba(80,60,20,0.3)' }} />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-xs font-serif truncate ${isStudied ? 'text-amber-200' : 'text-amber-500/70'}`}>
                        {piece.reference}
                      </p>
                      <p className={`text-[10px] truncate ${isStudied ? 'text-amber-500/50' : 'text-amber-600/50'}`}>
                        {piece.keyword || piece.title}
                      </p>
                    </div>
                    <span className="text-[8px] font-mono px-1.5 py-0.5 rounded flex-shrink-0"
                      style={{
                        background: `${piece.color}${isStudied ? '15' : '08'}`,
                        color: `${piece.color}${isStudied ? '70' : '25'}`,
                      }}>
                      {piece.piece_type === 'corner' ? 'CORNER' : 'BORDER'}
                    </span>
                    <svg className="w-3 h-3 text-amber-800/20 group-hover:text-amber-500/50 transition-colors flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Completion Rewards Section */}
          <div className="px-5 pb-8">
            <div className="flex items-center gap-2 mb-3">
              <p className="text-[9px] tracking-[0.2em] uppercase font-mono text-amber-500/70">Completion Rewards</p>
              {isFrameComplete && rewards?.unlocked && (
                <span className="text-[8px] font-mono px-1.5 py-0.5 rounded-full bg-emerald-900/30 text-emerald-400 border border-emerald-800/30">UNLOCKED</span>
              )}
            </div>

            {/* Unlock Celebration */}
            {showUnlockCelebration && (
              <div className="mb-4 p-4 rounded-xl border text-center fade-up" data-testid="unlock-celebration"
                style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.1), rgba(217,119,6,0.05))', borderColor: 'rgba(16,185,129,0.3)' }}>
                <div className="text-2xl mb-2" style={{ color: '#fcd34d' }}>&#10024;</div>
                <p className="text-sm font-serif text-emerald-300 font-semibold mb-1">Puzzle Complete!</p>
                <p className="text-[11px] text-amber-200/60 font-serif">You've unlocked exclusive bonus content. The picture is revealed.</p>
              </div>
            )}

            <div className="space-y-2">
              {(rewards?.rewards || [
                { reward_id: 'author-notes', title: "Author's Personal Notes", description: 'Loading...', icon: 'scroll' },
                { reward_id: 'prayer-guide', title: 'The Watchman\'s Prayer', description: 'Loading...', icon: 'flame' },
                { reward_id: 'scholars-key', title: 'The Scholar\'s Key', description: 'Loading...', icon: 'key' },
              ]).map((reward) => {
                const unlocked = rewards?.unlocked;
                const isExpanded = expandedReward === reward.reward_id;

                return (
                  <div key={reward.reward_id} data-testid={`reward-${reward.reward_id}`}>
                    <button
                      onClick={() => unlocked && setExpandedReward(isExpanded ? null : reward.reward_id)}
                      disabled={!unlocked}
                      className={`w-full text-left flex items-start gap-3 px-4 py-3 rounded-xl transition-all duration-300 border ${
                        unlocked ? 'hover:bg-amber-900/10 cursor-pointer' : 'cursor-default opacity-60'
                      }`}
                      style={{
                        background: unlocked
                          ? (isExpanded ? 'rgba(16,185,129,0.06)' : 'rgba(15,10,5,0.4)')
                          : 'rgba(10,8,4,0.3)',
                        borderColor: unlocked
                          ? (isExpanded ? 'rgba(16,185,129,0.2)' : 'rgba(120,80,20,0.1)')
                          : 'rgba(60,40,10,0.08)',
                      }}
                    >
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        unlocked ? 'text-amber-400' : 'text-amber-900/30'
                      }`} style={{
                        background: unlocked ? 'rgba(217,119,6,0.12)' : 'rgba(20,15,8,0.3)',
                        border: `1px solid ${unlocked ? 'rgba(217,119,6,0.2)' : 'rgba(60,40,10,0.1)'}`,
                      }}>
                        {unlocked ? (ICON_MAP[reward.icon] || ICON_MAP.scroll) : (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={`text-xs font-serif font-semibold ${unlocked ? 'text-amber-200' : 'text-amber-600/60'}`}>
                          {reward.title}
                        </p>
                        <p className={`text-[10px] mt-0.5 leading-relaxed ${unlocked ? 'text-amber-500/50' : 'text-amber-600/50'}`}>
                          {unlocked ? reward.description : `Complete all ${totalCount} pieces to unlock`}
                        </p>
                      </div>
                      {unlocked && (
                        <svg className={`w-3.5 h-3.5 flex-shrink-0 mt-1 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                          style={{ color: isExpanded ? '#34d399' : 'rgba(120,80,20,0.3)' }}
                          fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </button>

                    {/* Expanded Content */}
                    {isExpanded && unlocked && reward.content && (
                      <div className="mt-1 mx-2 px-4 py-4 rounded-xl border fade-up" data-testid={`reward-content-${reward.reward_id}`}
                        style={{ background: 'rgba(10,8,4,0.6)', borderColor: 'rgba(16,185,129,0.1)' }}>
                        <div className="prose prose-sm max-w-none">
                          {reward.content.split('\n').map((line, i) => {
                            if (!line.trim()) return <div key={i} className="h-3" />;
                            const isHeading = line === line.toUpperCase() && line.length > 3 && line.length < 80;
                            const isBold = line.startsWith('For the ') || line.startsWith('Father') || line.startsWith('Make me');
                            return (
                              <p key={i} className={`mb-1.5 leading-relaxed ${
                                isHeading ? 'text-[11px] tracking-[0.15em] font-mono text-amber-500/70 mt-3 mb-2' :
                                isBold ? 'text-[11px] font-serif text-amber-300/80 font-semibold' :
                                'text-[11px] font-serif text-amber-200/50'
                              }`}>
                                {line}
                              </p>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {!isComplete && (
              <div className="mt-3 text-center">
                <p className="text-[10px] text-amber-500/60 font-serif italic">
                  Study {totalCount - studiedCount} more piece{totalCount - studiedCount > 1 ? 's' : ''} to unlock these treasures
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Share Journey Card Modal */}
      {showShareCard && (
        <ShareJourneyCard
          pieces={pieces}
          studiedPieces={studiedPieces}
          discount={discount}
          onClose={() => setShowShareCard(false)}
        />
      )}
    </>
  );
}
